import React, { useState, useEffect } from "react";
import { Modal, Form, ConfigProvider, Tabs } from "antd";
import { useSelector } from "react-redux";
import { createInviteLink } from "../../api/invite_link_endpoints";
import { emptyProductScopes } from "../../constants/moduleScopes";
import { getCallerMarketScope } from "../../utils/callerMarketScope";
import { parseEmailInput } from "../../utils/emailChips";
import { useWorkspaceMarketOptions } from "../../hooks/UserManagementPage/useWorkspaceMarketOptions";
import ScopePicker from "./inviteUsers/ScopePicker";
import RolePicker from "./inviteUsers/RolePicker";
import EmailInviteTab from "./inviteUsers/EmailInviteTab";
import LinkGenerationTab from "./inviteUsers/LinkGenerationTab";
import LinkPreview from "./inviteUsers/LinkPreview";
import "./InviteUsersModal.css";

const InviteUsersModal = ({
  open,
  onOk,
  onCancel,
  onGenerateLink,
  onGenerateGroupLink,
  form,
  // Server-computed grant ladder for the selected workspace, threaded from the
  // page's roster fetch. `null` until it loads; RolePicker disables everything
  // in that state rather than guessing.
  assignableRoles,
  className = "",
  width = 720,
}) => {
  // Get workspace data from Redux
  const { allWorkspaces } = useSelector((state) => state.workspace);
  const { selectedWorkspace, userData } = useSelector((state) => state.auth);

  // Local state for modal
  const [activeTab, setActiveTab] = useState("email");
  const [emailSearchValue, setEmailSearchValue] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [role, setRole] = useState("admin");
  // Per-module country scope: one country list per dashboard.
  const [productScopes, setProductScopes] = useState(emptyProductScopes());
  const [domainRestriction, setDomainRestriction] = useState("");
  const [linkPreview, setLinkPreview] = useState(null); // { url, domain, role, productScopes }
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkGenerating, setLinkGenerating] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab("email");
      setEmailSearchValue("");
      setSelectedEmails([]);
      setRole("admin");
      setProductScopes(emptyProductScopes());
      setDomainRestriction("");
      setLinkPreview(null);
      setLinkCopied(false);
    }
  }, [open]);

  // True when the user hasn't granted any module a single market.
  const hasNoModuleScope = Object.values(productScopes).every(
    (list) => !list || list.length === 0
  );

  // Function to get markets for current workspace. Constrained to the caller's
  // own markets when they're a market-scoped admin (getCallerMarketScope →
  // null means unrestricted). Backend enforces the same clamp; this is UX.
  const getMarketsForCurrentWorkspace = () => {
    const workspaceId = selectedWorkspace?.key || selectedWorkspace;
    // Find the aggregated workspace data from our processed workspaceOptions
    const workspaceOption = workspaceOptions.find(
      (option) => option.value === workspaceId
    );
    const allMarkets = workspaceOption?.workspace?.markets || [];
    const callerScope = getCallerMarketScope(userData, workspaceId);
    if (callerScope === null) return allMarkets;
    return allMarkets.filter((market) => callerScope.has(market));
  };

  const handleEmailInputChange = (e) => {
    setEmailSearchValue(e.target.value);
  };

  // Parse (pure) then apply to state: add valid new emails, keep invalid tokens
  // in the input box.
  const processEmailInput = (inputValue) => {
    const { validEmails, invalidEmails } = parseEmailInput(inputValue, selectedEmails);
    if (validEmails.length > 0) {
      setSelectedEmails([...selectedEmails, ...validEmails]);
    }
    setEmailSearchValue(invalidEmails.join(", "));
    return { validEmails, invalidEmails };
  };

  // Update user name in the selected emails list
  const handleNameChange = (email, newName) => {
    setSelectedEmails(
      selectedEmails.map((user) =>
        user.email === email ? { ...user, name: newName } : user
      )
    );
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      processEmailInput(emailSearchValue);
    }
  };

  const handleEmailBlur = () => {
    if (emailSearchValue.trim()) {
      processEmailInput(emailSearchValue);
    }
  };

  const handleEmailRemove = (emailToRemove) => {
    Modal.confirm({
      title: "Remove User?",
      content: `Are you sure you want to remove ${emailToRemove} from the invite list?`,
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: () => {
        setSelectedEmails(
          selectedEmails.filter((email) => email.email !== emailToRemove)
        );
      },
    });
  };

  // Role change handler
  const handleRoleChange = (newRole) => {
    setRole(newRole);
    // If changing role to super_admin, clear scope selections since they have full access
    if (newRole === "super_admin") {
      setProductScopes(emptyProductScopes());
    }
  };

  const buildFormData = () => {
    const workspaceId = selectedWorkspace?.key || selectedWorkspace;

    // super_admin gets full access (product_scopes is set null by the backend),
    // so the per-module selections are irrelevant for that role.
    return {
      emails: selectedEmails,
      workspaceAssignments: [
        {
          workspaceId: workspaceId,
          role: role,
          productScopes: { ...productScopes },
        },
      ],
    };
  };

  const handleOk = () => {
    const formData = buildFormData();
    onOk(formData);
  };

  const handleGenerateLinkClick = () => {
    const formData = buildFormData();
    onGenerateLink(formData);
  };

  const buildLinkFormData = () => {
    const workspaceId = selectedWorkspace?.key || selectedWorkspace;

    // Normalize domain: strip leading @ if present
    const normalizedDomain = domainRestriction.trim().replace(/^@/, "");

    return {
      domainRestriction: normalizedDomain || null,
      workspaceAssignments: [
        {
          workspaceId: workspaceId,
          role: role,
          productScopes: { ...productScopes },
        },
      ],
    };
  };

  const handleGenerateGroupLinkClick = async () => {
    const linkData = buildLinkFormData();
    const assignment = linkData.workspaceAssignments[0];

    // Role values are already backend form (super_admin / admin / normal_user).
    const backendRole = assignment.role;

    // Build product_scopes with per-module country lists (null for super_admin).
    const product_scopes =
      backendRole === "super_admin" ? null : { ...assignment.productScopes };

    setLinkGenerating(true);
    try {
      const result = await createInviteLink({
        id_client: assignment.workspaceId,
        role: backendRole,
        product_scopes,
        domain_restriction: linkData.domainRestriction,
      });
      const baseUrl = window.location.origin;
      setLinkPreview({
        url: `${baseUrl}/join/${result.token}`,
        domain: result.domain_restriction,
        role: role,
        productScopes: assignment.productScopes,
      });
      setLinkCopied(false);
      if (onGenerateGroupLink) onGenerateGroupLink(result);
    } catch (err) {
      console.error("Failed to generate invite link:", err);
    } finally {
      setLinkGenerating(false);
    }
  };

  const handleCopyLink = () => {
    if (linkPreview?.url) {
      navigator.clipboard.writeText(linkPreview.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleBackToLinkForm = () => {
    setLinkPreview(null);
    setLinkCopied(false);
  };

  const handleCancel = () => {
    setActiveTab("email");
    setEmailSearchValue("");
    setSelectedEmails([]);
    setRole("admin");
    setProductScopes(emptyProductScopes());
    setDomainRestriction("");
    setLinkPreview(null);
    setLinkCopied(false);
    setLinkGenerating(false);
    onCancel();
  };

  // Aggregate raw workspace rows into unique per-id_client select options.
  const workspaceOptions = useWorkspaceMarketOptions(allWorkspaces);

  return (
    <ConfigProvider
      theme={{
        components: {
          TreeSelect: {
            borderRadius: 16,
          },
          Modal: {
            borderRadiusLG: 16,
          },
        },
      }}
    >
      <Modal
        title={
          <div className="flex justify-between items-center pb-4 border-b border-gray-100">
            <h2 className="text-gray-900 text-xl font-semibold font-['Inter'] leading-tight">
              Invite Users
            </h2>
          </div>
        }
        open={open}
        onCancel={handleCancel}
        width={width}
        centered
        className={className}
        maskClosable={false}
        footer={(() => {
          if (activeTab === "email") {
            const isDisabled =
              selectedEmails.length === 0 ||
              (role !== "super_admin" && hasNoModuleScope);
            return (
              <div className="flex justify-end items-center gap-2 pt-2">
                <button
                  onClick={handleCancel}
                  className="border border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 rounded-lg h-10 px-6 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateLinkClick}
                  disabled={isDisabled}
                  className={`border rounded-lg h-10 px-6 font-medium transition-colors flex items-center gap-1.5 ${
                    isDisabled
                      ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                      : "border-blue-600 text-blue-600 hover:bg-blue-50 cursor-pointer"
                  }`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  Generate Links
                </button>
                <button
                  onClick={handleOk}
                  disabled={isDisabled}
                  className={`rounded-lg h-10 px-6 font-medium transition-colors ${
                    isDisabled
                      ? "bg-gray-300 text-white cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                  }`}
                >
                  Send Invitations
                </button>
              </div>
            );
          }

          // Link Generation tab footer — preview state
          if (linkPreview) {
            return (
              <div className="flex justify-between items-center pt-2">
                <button
                  onClick={handleBackToLinkForm}
                  className="border border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 rounded-lg h-10 px-6 font-medium transition-colors flex items-center gap-1.5"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-10 px-6 font-medium transition-colors cursor-pointer"
                >
                  Done
                </button>
              </div>
            );
          }

          const isLinkDisabled = role !== "super_admin" && hasNoModuleScope;
          return (
            <div className="flex justify-end items-center gap-2 pt-2">
              <button
                onClick={handleCancel}
                className="border border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 rounded-lg h-10 px-6 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateGroupLinkClick}
                disabled={isLinkDisabled || linkGenerating}
                className={`border rounded-lg h-10 px-6 font-medium transition-colors flex items-center gap-1.5 ${
                  isLinkDisabled || linkGenerating
                    ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                    : "border-blue-600 text-blue-600 hover:bg-blue-50 cursor-pointer"
                }`}
              >
                {linkGenerating ? (
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                )}
                {linkGenerating ? "Generating…" : "Generate Link"}
              </button>
            </div>
          );
        })()}
      >
        <div className="pt-2 pb-6 flex flex-col gap-6">
          {/* Link preview — shown after Generate Link is clicked */}
          {linkPreview ? (
            <LinkPreview
              linkPreview={linkPreview}
              linkCopied={linkCopied}
              onCopy={handleCopyLink}
            />
          ) : (
          <>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="invite-modal-tabs"
            items={[
              { key: "email", label: "Email Invite" },
              { key: "link", label: "Link Generation" },
            ]}
          />
          <Form
            form={form}
            layout="vertical"
            className="w-full flex flex-col gap-6"
          >
            {/* Tab-specific content */}
            {activeTab === "email" ? (
              <>
            {/* Email Invite Tab — email chips + optional per-user names */}
            <EmailInviteTab
              selectedEmails={selectedEmails}
              emailSearchValue={emailSearchValue}
              onInputChange={handleEmailInputChange}
              onKeyDown={handleEmailKeyDown}
              onBlur={handleEmailBlur}
              onRemove={handleEmailRemove}
              onNameChange={handleNameChange}
            />
              </>
            ) : (
              /* Link Generation Tab — domain restriction */
              <LinkGenerationTab
                domainRestriction={domainRestriction}
                onChange={setDomainRestriction}
              />
            )}

            {/* Role Selection */}
            <RolePicker
              role={role}
              onChange={handleRoleChange}
              assignableRoles={assignableRoles}
            />

            {/* Scope Selection — ScopePicker renders nothing for super_admin */}
            <ScopePicker
              role={role}
              markets={getMarketsForCurrentWorkspace()}
              productScopes={productScopes}
              onChange={setProductScopes}
            />

            {/* Super Admin Notice */}
            {role === "super_admin" && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-5 h-5 flex items-center justify-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-blue-600"
                  >
                    <path
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-blue-800 text-sm font-medium font-['Inter']">
                    Super Admin Access
                  </span>
                  <span className="text-blue-600 text-xs font-normal font-['Inter']">
                    This role has access to all scopes and features in
                    the workspace
                  </span>
                </div>
              </div>
            )}
          </Form>
          </>
          )}
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default InviteUsersModal;
